import { FC, Fragment } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import {
  createUrlWithSourceQueryParams,
  useAmplitude,
} from '@crehana/analytics';
import ArrowIosDownOutline from '@crehana/icons/ArrowIosDownOutline';
import ArrowIosUpOutline from '@crehana/icons/ArrowIosUpOutline';
import Phone from '@crehana/icons/Phone';
import {
  Col,
  Collapse,
  colors,
  Container,
  PrimaryButton,
  Row,
} from '@crehana/ui';

import { baseRedirectUrlToContactForm } from '@/views/B2BLanding/constants';
import { AMPLITUDE_TRACKS } from '@/views/B2BLanding/events';
import { buildStaticResourceUtms } from '@/views/B2BLanding/utils/buildStaticResourceUtms';
import { joinQueryParams } from '@/views/B2BLanding/utils/joinQueryParams';

import { itemDataType } from '../../Header.data';
import { BaseMainMenuTypes } from '../../Header.models';
import useHeaderTranslation from '../../hooks/useHeaderTranslation';

const { primaryMain, black } = colors;

interface DropdownMenuProps extends BaseMainMenuTypes {
  isDesktopOpen: boolean;
  itemData: itemDataType | null;
  closeDropdownMenu: () => void;
}

const DropdownMenu: FC<React.PropsWithChildren<DropdownMenuProps>> = ({
  isDesktopOpen,
  isMobileOpen,
  itemData,
  items,
  itemOpen,
  toggleItem,
  showCallButton,
  setOpenContactDialog,
  closeDropdownMenu,
}) => {
  const router = useRouter();
  const { logEvent } = useAmplitude();
  const { t } = useHeaderTranslation();

  const hasMore = () => (itemData?.items ? itemData?.items.length > 4 : false);

  const getFreeTrialUrl = () => {
    if (typeof window !== 'undefined') {
      return createUrlWithSourceQueryParams({
        url: joinQueryParams(
          baseRedirectUrlToContactForm,
          router.asPath,
          buildStaticResourceUtms({
            urlResource: router.query.slug as string,
            term: 'agenda-demo',
          }),
        ),
        source: 'Header - Agenda una demo',
      });
    }
    return '';
  };

  return (
    <Fragment>
      <div className="hidden lg:block relative overflow-hidden">
        <Collapse open={isDesktopOpen} className="flex w-full bg-white">
          <Container>
            {itemData?.groups && (
              <Row itemsPerRow={{ md: 2 }} className=" py-40">
                {itemData?.groups?.map(group => (
                  <Col key={group.id}>
                    <h3 className="text-base-lighter font-label">
                      {t(group.title)}
                    </h3>
                    <Row
                      className="mt-16"
                      gutter={{ md: 32 }}
                      itemsPerRow={{ md: 2 }}
                    >
                      {group.subItems?.map(subItem => {
                        return (
                          <Col key={subItem.id}>
                            <Link
                              href={subItem.link}
                              className="flex items-center"
                              onClick={() => {
                                closeDropdownMenu();
                                if (itemOpen) {
                                  logEvent({
                                    event: AMPLITUDE_TRACKS.MENU_OPTION_CLICKED,
                                    data: {
                                      Section: t(subItem.name),
                                    },
                                  });
                                }
                              }}
                            >
                              <div className="h-24 w-24 mr-16 relative">
                                {group.id === 'academies' && (
                                  <div
                                    className="w-20 h-20 bg-primary-lighter absolute bottom-0 left-0 z-0"
                                    css={{
                                      transform: 'translate(-6px, 6px)',
                                    }}
                                  />
                                )}
                                <subItem.icon
                                  className="z-1 relative"
                                  size={24}
                                  color={black}
                                  secondaryColor={
                                    group.id === 'academies'
                                      ? black
                                      : primaryMain
                                  }
                                />
                              </div>
                              <div className="flex flex-col items-start">
                                <h3 className="font-link text-14 text-base-main mb-4">
                                  {t(subItem.name)}
                                </h3>
                                <p className="font-body4 text-gray-main-dark-mode text-left">
                                  {t(subItem.description)}
                                </p>
                              </div>
                            </Link>
                          </Col>
                        );
                      })}
                    </Row>
                  </Col>
                ))}
              </Row>
            )}
            {itemData?.items && (
              <Row
                gutter={{ md: 32 }}
                itemsPerRow={{ md: 2 }}
                className="py-40"
              >
                {itemData?.items?.map(
                  ({ id, link, name, description, icon: Icon }) => {
                    return (
                      <Col key={id}>
                        <Link
                          href={link}
                          className={`flex ${
                            hasMore() ? 'items-center' : 'flex-col items-start'
                          }`}
                          onClick={() => {
                            closeDropdownMenu();
                            if (itemOpen) {
                              logEvent({
                                event: AMPLITUDE_TRACKS.MENU_OPTION_CLICKED,
                                data: {
                                  Section: t(name),
                                },
                              });
                            }
                          }}
                        >
                          {hasMore() ? (
                            <div className="h-24 w-24 mr-16">
                              <Icon size={24} />
                            </div>
                          ) : (
                            <Icon className="mb-16" size={48} />
                          )}
                          <div className="flex flex-col items-start">
                            <h3 className="font-link text-14 text-base-main mb-4">
                              {t(name)}
                            </h3>
                            <p className="font-body4 text-gray-main-dark-mode text-left">
                              {t(description)}
                            </p>
                          </div>
                        </Link>
                      </Col>
                    );
                  },
                )}
              </Row>
            )}
          </Container>
        </Collapse>
      </div>
      <div className="block lg:hidden">
        <Collapse open={isMobileOpen} className="bg-white flex flex-col">
          <Container className="flex flex-col pb-20">
            {items.map(item => {
              const isCollapsible =
                item.type === 'items' || item.type === 'groups';

              return (
                <Row key={item.key} className="flex flex-col items-start">
                  <Col>
                    <button
                      type="button"
                      className="flex items-center"
                      onClick={() => {
                        if (!item.data.link) {
                          toggleItem(item.key);
                        } else {
                          closeDropdownMenu();
                          logEvent({
                            event: AMPLITUDE_TRACKS.MENU_OPTION_CLICKED,
                            data: {
                              Section: t(item.name),
                            },
                          });
                          router.push(item.data.link);
                        }
                      }}
                      aria-label={t(item.name)}
                    >
                      <p
                        className={`font-link py-16 mr-8 ${
                          itemOpen === item.key ? 'text-primary-main' : ''
                        }`}
                      >
                        {t(item.name)}
                      </p>
                      {isCollapsible && (
                        <Fragment>
                          {itemOpen === item.key ? (
                            <ArrowIosUpOutline color={primaryMain} size={24} />
                          ) : (
                            <ArrowIosDownOutline size={24} />
                          )}
                        </Fragment>
                      )}
                    </button>
                  </Col>
                  <Col>
                    <Collapse open={itemOpen === item.key}>
                      <div className="flex flex-col w-full">
                        {itemData?.groups?.map((group, index) => {
                          return (
                            <>
                              {index > 0 && (
                                <div className="flex w-full h-1 bg-gray-light my-20" />
                              )}
                              {group.subItems?.map(subItem => (
                                <Col key={subItem.id}>
                                  <Link
                                    href={subItem.link}
                                    onClick={() => {
                                      closeDropdownMenu();
                                      logEvent({
                                        event:
                                          AMPLITUDE_TRACKS.MENU_OPTION_CLICKED,
                                        data: {
                                          Section: t(subItem.name),
                                        },
                                      });
                                    }}
                                    className="font-body4 relative text-gray-main-dark-mode py-12 w-full focus:outline-none text-left flex items-center"
                                  >
                                    {group.id === 'academies' && (
                                      <div
                                        className="w-20 h-20 bg-primary-lighter absolute bottom-0 left-0 z-0"
                                        css={{
                                          transform: 'translate(-5px, -6px)',
                                        }}
                                      />
                                    )}
                                    {subItem.icon && (
                                      <subItem.icon
                                        className="mr-16 z-1 relative"
                                        size={24}
                                        color={black}
                                        secondaryColor={
                                          group.id === 'academies'
                                            ? black
                                            : primaryMain
                                        }
                                      />
                                    )}
                                    {t(subItem.name)}
                                  </Link>
                                </Col>
                              ))}
                            </>
                          );
                        })}
                        {itemData?.items?.map(subItem => (
                          <Col key={subItem.id}>
                            <Link
                              href={subItem.link}
                              onClick={() => {
                                closeDropdownMenu();
                                logEvent({
                                  event: AMPLITUDE_TRACKS.MENU_OPTION_CLICKED,
                                  data: {
                                    Section: t(subItem.name),
                                  },
                                });
                              }}
                              className="font-body4 text-gray-main-dark-mode py-12 w-full focus:outline-none text-left flex items-center"
                            >
                              {subItem.icon && (
                                <subItem.icon className="mr-16" />
                              )}
                              {t(subItem.name)}
                            </Link>
                          </Col>
                        ))}
                      </div>
                    </Collapse>
                  </Col>
                </Row>
              );
            })}
            {showCallButton ? (
              <PrimaryButton
                leftIcon={{
                  component: Phone,
                  size: 24,
                }}
                onClick={() => {
                  logEvent({
                    event: AMPLITUDE_TRACKS.MAIN_HEADER_CALL_US,
                    data: {
                      'CTA Type': 'button',
                      'CTA Location': 'header mobile',
                      'CTA Text': t('HEADER_CALL_BUTTON'),
                    },
                  });
                  setOpenContactDialog(true);
                }}
                label={t('HEADER_CALL_BUTTON')}
                className="mt-32"
                full
              />
            ) : (
              <PrimaryButton
                label={t('HEADER_FREE_TRIAL_BUTTON')}
                onClick={() => {
                  closeDropdownMenu();
                  logEvent({
                    event: AMPLITUDE_TRACKS.WANTS_TO_KNOW_MORE,
                    data: {
                      'CTA Type': 'button',
                      'CTA Location': 'header',
                      'CTA Text': t('HEADER_FREE_TRIAL_BUTTON'),
                    },
                  });

                  router.push(getFreeTrialUrl());
                }}
                className="mt-32"
                full
              />
            )}
          </Container>
        </Collapse>
      </div>
    </Fragment>
  );
};

export default DropdownMenu;
